import { useRef, useState, type CSSProperties } from "react";
import { Icon, type IconName } from "@/components/ui/Icon";
import { Popover } from "@/components/ui/Popover";
import { RenameColumnDialog } from "@/components/dialogs/RenameColumnDialog";
import { ConfirmDialog } from "@/components/dialogs/ConfirmDialog";
import { StatusOptionsDialog } from "@/components/dialogs/StatusOptionsDialog";
import { useDeleteColumn } from "@/hooks/useDeleteColumn";
import { isWorkflowStatusColumn } from "@/lib/statusRoles";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import { isWorkflowKind } from "@/lib/boardView";
import type { ViewColumn } from "@/lib/boardView";
import type { BoardKind, UUID } from "@/lib/types";

interface Props {
  boardId: UUID;
  column: ViewColumn;
  boardKind: BoardKind;
  /** Workspace owner/admin — same gate as inline "+ Neue Option". */
  canManageOptions?: boolean;
  /** Header row is hovered — lifts the trigger to full strength. */
  headerHover?: boolean;
}

export function ColumnMenu({
  boardId,
  column,
  boardKind,
  canManageOptions = false,
  headerHover = false,
}: Props) {
  const columnId = column.id as UUID;
  const columnName = column.name;
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const deleteMutation = useDeleteColumn();

  // Per-option management is offered for every status column EXCEPT a ticket
  // board's action-driven workflow Status (editing its role-bearing options
  // would break the Annehmen/Lösen transitions — it stays locked).
  const showManageOptions =
    canManageOptions &&
    column.type === "status" &&
    !(isWorkflowKind(boardKind) && isWorkflowStatusColumn(column));

  async function onConfirmDelete() {
    try {
      await deleteMutation.mutateAsync({ boardId, columnId });
      setDeleteOpen(false);
      showToast(t.columnDeletedToast, "success");
    } catch (err) {
      showToast(err instanceof Error ? err.message : t.unknownError, "error");
    }
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        title={t.more}
        style={{
          background: open
            ? "var(--surface-elev)"
            : headerHover
              ? "var(--surface-soft)"
              : "transparent",
          border: "none",
          padding: 3,
          borderRadius: 5,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          // Hover-only: hidden at rest (space reserved → no layout jump), shown
          // when the header row is hovered or the menu is open. Keeps the bolder
          // 17px trigger, drops the always-visible look.
          opacity: open || headerHover ? 1 : 0,
          pointerEvents: open || headerHover ? "auto" : "none",
          transition: "opacity .1s, background .1s, color .1s",
        }}
      >
        <Icon
          name="dotsV"
          size={17}
          stroke={2.4}
          color={open || headerHover ? "var(--ink-1)" : "var(--ink-2)"}
        />
      </button>
      {open ? (
        <Popover
          anchor={triggerRef.current}
          onClose={() => setOpen(false)}
          width={200}
          align="right"
        >
          <MenuItem
            icon="edit"
            label={t.renameColumn}
            onClick={() => {
              setOpen(false);
              setRenameOpen(true);
            }}
          />
          {showManageOptions ? (
            <MenuItem
              icon="sliders"
              label={t.manageStatusOptions}
              onClick={() => {
                setOpen(false);
                setOptionsOpen(true);
              }}
            />
          ) : null}
          <Divider />
          <MenuItem
            icon="trash"
            label={t.deleteColumn}
            tone="danger"
            onClick={() => {
              setOpen(false);
              setDeleteOpen(true);
            }}
          />
        </Popover>
      ) : null}

      <RenameColumnDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        boardId={boardId}
        columnId={columnId}
        initialName={columnName}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t.deleteColumnTitle}
        message={t.deleteColumnMessage.replace("{name}", columnName)}
        confirmLabel={t.deleteColumn}
        tone="danger"
        busy={deleteMutation.isPending}
        onConfirm={onConfirmDelete}
      />

      {showManageOptions ? (
        <StatusOptionsDialog
          open={optionsOpen}
          onOpenChange={setOptionsOpen}
          boardId={boardId}
          column={column}
        />
      ) : null}
    </>
  );
}

function Divider() {
  return <div style={{ height: 1, background: "var(--hairline)", margin: "4px 0" }} />;
}

function MenuItem({
  icon,
  label,
  onClick,
  tone = "default",
}: {
  icon: IconName;
  label: string;
  onClick: () => void;
  tone?: "default" | "danger";
}) {
  const itemStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    padding: "8px 10px",
    background: "transparent",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 13,
    color: tone === "danger" ? "var(--danger)" : "var(--ink-1)",
    fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
    textAlign: "left",
  };
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      style={itemStyle}
    >
      <Icon name={icon} size={14} color={tone === "danger" ? "var(--danger)" : "var(--ink-3)"} />
      {label}
    </button>
  );
}
